import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import { object, string } from 'yup';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form } from 'formik';
import difference from 'lodash/difference';
import pick from 'lodash/pick';
import useSWR from 'swr';
import { patch, prepareAttrs } from 'utils/api';
import { CgTemplate } from "react-icons/cg";

import TextField from 'app/form/formik/text/index';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import useEntity from 'entities/hooks/use-entity';
import { Flex, Text, Box } from 'rebass';
import Modal from 'app/modal/v3';
import Grid from 'app/grid/index';
import TemplateRenderer from './components/TemplateRenderer';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import RenderFrontCover from './components/RenderFrontCover';

function FrontCoverTemplatesComponent({ frontCoverTemplates, frontCoverEntity, revalidateBook, selectedTemplate, firstBookId }) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function onTemplateSelection(template) {
        if (template?.id !== selectedTemplate?.id) {
            setIsSubmitting(true);

            await patch(`/v3/books/${firstBookId}/front_covers/${frontCoverEntity?.id}`, {
                data: {
                    attributes: {
                        'front_cover_template_id': template.id,
                    },
                },
            });

            revalidateBook();
            setIsSubmitting(false);
        }
    }

    return <BufferingOverlay isBuffering={isSubmitting}>
        <Grid
            gridTemplateColumns={`repeat(auto-fit, minmax(166px, 166px))`}
            gridGap={[1, 2]}
        >
            {frontCoverTemplates.map((temp) => <TemplateRenderer
                example={temp?.relationships?.example?.data}
                isSelected={selectedTemplate?.id === temp?.id}
                onTemplateSelection={() => onTemplateSelection(temp)}
            />)}
        </Grid>
    </BufferingOverlay>
}

function BookForm({ isSubmitting, handleSubmit, firstBookId, frontCoverTemplates, revalidateBook, setIsDefaultStepSet, ...props }) {
    const ref = useRef();
    const [showTemplate, setShowTemplate] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [posterSectionWidth, setPosterSectionWidth] = useState(0);
    const [parentHeight, setParentHeight] = useState(null);

    const { entity: bookEntity } = useEntity({ type: 'book', id: firstBookId })
    const { entity: frontCoverEntity } = useEntity({ id: bookEntity?.['front-cover']?.id, type: 'front-covers' })
    const { data: front_cover_templates_data, revalidate: revalidateFrontCoverTemplatesData } = useSWR((firstBookId && frontCoverEntity?.id) ? `/v3/books/${firstBookId}/front_covers/${frontCoverEntity?.id}` : null);

    const frontCoverLayersData = (front_cover_templates_data?.data?.included || []).filter(({ type }) => type === 'cover-layers')

    useEffect(() => {
        if (frontCoverEntity?.['front-cover-template']?.id)
            setSelectedTemplate(frontCoverTemplates.find(({ id }) => id === frontCoverEntity?.['front-cover-template']?.id))
        else
            setSelectedTemplate(null)
    }, [frontCoverEntity?.['front-cover-template']?.id, frontCoverTemplates])

    useEffect(() => {
        revalidateFrontCoverTemplatesData()
    }, [selectedTemplate])

    return <div ref={ref}>
        <Text fontSize="16px" fontFamily="Poppins" fontWeight={400} lineHeight="150%" letterSpacing="-0.32px" textAlign="left" mb={3} mt={2} color="#737373" >
            Add a title and photo for you book and select a cover template. You can make changes later.<br />
            Click next step to move on.
        </Text>
        <Form>
            <Flex flexDirection={["column-reverse", "row"]} sx={{ gap: 3 }} justifyContent="space-between">
                <Flex flexDirection="column" width={["100%", "60%"]} ref={(el) => {
                    if (!el) return;
                    setPosterSectionWidth(el.getBoundingClientRect().width);
                    setParentHeight(el.getBoundingClientRect().height);
                }}>
                    <Flex justifyContent="space-between" alignItems="center" mb={3}>
                        <Text fontFamily="Poppins" fontSize={14} fontWeight={500} lineHeight="165%" letterSpacing="0.16px" color="#0E0E0E">
                            Front Cover
                        </Text>
                        <Flex alignItems="center" onClick={() => setShowTemplate(true)} sx={{ display: ['flex', 'none'], gap: 1, cursor: 'pointer' }}>
                            <CgTemplate color="#64748B" size={16} />
                            <Text fontFamily="Poppins" fontSize={16} fontWeight={500} lineHeight="20px" letterSpacing="-0.096px" color="#64748B">
                                Select a template
                            </Text>
                        </Flex>
                    </Flex>
                    <RenderFrontCover setIsDefaultStepSet={setIsDefaultStepSet} values={props.values} selectedTemplate={selectedTemplate} posterSectionWidth={posterSectionWidth} parentHeight={parentHeight} fronCoverLayers={frontCoverLayersData || []} firstBookId={firstBookId} />
                </Flex>
                <Box width={["100%", "50%"]}>
                    <Field mb={5}
                        onBlur={handleSubmit}
                        component={TextField} name="title" label="Main Title*" showError width="100%" />
                    <Field
                        component={TextField}
                        onBlur={handleSubmit}
                        name="sub-title"
                        label="Subtitle"
                        showError
                        width="100%"
                    />
                    {/* <Field
                        name="cover"
                        component={CoverField}
                        // currentCover={currentCoverId}
                        width={rect && rect.width}
                    /> */}
                </Box>
            </Flex>
            {showTemplate && <Modal title="Select template" close={{ onClose: () => setShowTemplate(false) }} footerOffset={60}>
                <FrontCoverTemplatesComponent frontCoverEntity={frontCoverEntity} revalidateBook={revalidateBook} firstBookId={firstBookId} selectedTemplate={selectedTemplate} frontCoverTemplates={frontCoverTemplates} />
            </Modal>}
            <Flex flexDirection="column" mt={5} width="100%" sx={{ display: ['none !important', 'flex !important'] }}>
                <Text fontFamily="Poppins" fontSize={16} fontWeight={500} lineHeight="20px" letterSpacing="-0.096px" mb={3} mt={2} color="#64748B">
                    Select a template
                </Text>
                <FrontCoverTemplatesComponent frontCoverEntity={frontCoverEntity} revalidateBook={revalidateBook} firstBookId={firstBookId} selectedTemplate={selectedTemplate} frontCoverTemplates={frontCoverTemplates} />
            </Flex>
        </Form>
    </div >
}

const EditBookDetails = ({ firstBookId, setIsDefaultStepSet, frontCoverTemplates, babysFullName, revalidateBook }) => {
    const type = 'book';
    const { receive } = useEntityActionCreators();
    const { entity: collection } = useEntity({ type, id: firstBookId });

    if (isEmpty(collection)) return false;

    return (
        <Box maxWidth={800} m="auto">
            <Formik
                initialValues={{
                    'title': collection.title || babysFullName,
                    'sub-title': collection['sub-title'] || 'First Year',
                    cover: '',
                }}
                onSubmit={async ({ cover, ...values }, { setSubmitting }) => {
                    const attributes = prepareAttrs(values);
                    const isDirty = difference(
                        Object.values(pick(collection, ['title', 'sub-title'])),
                        Object.values(pick(values, ['title', 'sub-title']))
                    );
                    // if (cover instanceof Blob) {
                    //     const coverImageId = await createCover({ id: firstBookId, type, cover });
                    //     if (coverImageId) {
                    //         attrs['cover-image'] = coverImageId;
                    //     }
                    // }
                    if (isDirty.length) {

                        const payload = await patch(
                            `/v3/books/${firstBookId}`,
                            {
                                data: {
                                    attributes,
                                },
                            }
                        );

                        receive({ payload });
                        setSubmitting(false);
                        // push(`/${type}/${id}`);
                    }
                    setIsDefaultStepSet(true);
                }}
                isInitialValid
                validationSchema={object().shape({
                    'title': string().required('Title is required'),
                })}
                render={formikProps => <BookForm {...formikProps} setIsDefaultStepSet={setIsDefaultStepSet} revalidateBook={revalidateBook} firstBookId={firstBookId} frontCoverTemplates={frontCoverTemplates} />}
            />
        </Box>
    );
};

export default EditBookDetails;
